/**
 * `useRealtimeQueryBridge` — the React binding for the realtime→query-cache
 * effects.
 *
 * The EFFECTS themselves are framework-agnostic and live in
 * `@/shared/lib/realtime-query-bindings` (Rule #16, Domain Purity: `lib/` is
 * plain TypeScript — no React, so the binding table stays unit-testable
 * without a renderer and cannot grow a hook dependency by accident). This
 * module is the thin React edge that owns the two framework concerns the pure
 * side must not: reading the `QueryClient` from context, and keeping the sink
 * referentially stable.
 */
import { applyRealtimeQueryBindings } from '@/shared/lib/realtime-query-bindings';
import type { Envelope } from '@letsgethome/realtime/events';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';

/**
 * The injected `onEnvelope` sink for `<PublicRealtimeProvider>`. Stable
 * across renders so the realtime listener registration does not churn.
 */
export function useRealtimeQueryBridge(): (envelope: Envelope) => void {
  const queryClient = useQueryClient();
  return useCallback(
    (envelope: Envelope) => applyRealtimeQueryBindings(envelope, queryClient),
    [queryClient]
  );
}
